/**
 * submit-coach-prompt.js
 * Shared use cases for coach prompt submission across legacy and service paths.
 */

const TEAM_TYPE_TO_LENS = {
  Scrum: 'scrum',
  Kanban: 'kanban',
  SAFe: 'safe',
  LeSS: 'less',
  XP: 'xp',
  Agile: 'agile',
};

export function createSubmitCoachPrompt({
  sendPrompt,
  buildPrecisionPrompt,
  fetchTeamDetails = async () => null,
  mapTeamTypeToLens = defaultMapTeamTypeToLens,
  now = defaultNow,
  metricsLogger = null,
  metricsStage = 'submitCoachPrompt',
  cacheKeyResolver = defaultCacheKeyResolver,
  teamCache = null,
} = {}) {
  if (typeof sendPrompt !== 'function') {
    throw new Error('sendPrompt is required');
  }
  if (typeof buildPrecisionPrompt !== 'function') {
    throw new Error('buildPrecisionPrompt is required');
  }

  const cache = teamCache instanceof Map ? teamCache : new Map();

  async function loadTeamContext(teamId) {
    if (!teamId) {
      return { teamInstructions: null, teamType: null, durationMs: 0 };
    }

    const cacheKey = cacheKeyResolver ? cacheKeyResolver(teamId) : teamId;
    if (cacheKey != null && cache.has(cacheKey)) {
      return { ...cache.get(cacheKey), durationMs: 0 };
    }

    const start = now();

    try {
      const details = await fetchTeamDetails(teamId);
      const context = {
        teamInstructions: details?.instructions ?? details?.notes ?? null,
        teamType: details?.team_type ?? details?.type ?? null,
      };

      if (cacheKey != null) {
        cache.set(cacheKey, context);
      }

      return { ...context, durationMs: elapsedSince(start, now) };
    } catch (error) {
      return { teamInstructions: null, teamType: null, durationMs: elapsedSince(start, now) };
    }
  }

  return async function submitCoachPrompt({
    message,
    persona = 'Agile Coach',
    lens = 'general',
    teamId = null,
    scopeId = null,
    teamName = null,
    attachments = [],
    notes = '',
    usePrecision = true,
    useIntelligenceContext = false,
  } = {}) {
    const trimmedMessage = typeof message === 'string' ? message.trim() : '';
    if (!trimmedMessage) {
      throw new Error('message is required');
    }

    const files = Array.isArray(attachments) ? attachments.slice() : [];
    const teamContext = await loadTeamContext(teamId);
    const effectiveLens = resolveLens(lens, teamContext.teamType, mapTeamTypeToLens);

    const normalizedTeamName = normalizeString(teamName);

    const precisionPrompt = callPrecisionBuilder(buildPrecisionPrompt, trimmedMessage, {
      persona,
      lens: effectiveLens,
      teamName: normalizedTeamName,
      teamId,
      files,
      teamInstructions: teamContext.teamInstructions,
      teamType: teamContext.teamType,
      usePrecision,
    });

    const submitPayload = {
      persona,
      lens: effectiveLens,
      teamId,
      scopeId,
      teamName: normalizedTeamName,
      precisionPrompt,
      originalMessage: trimmedMessage,
      attachments: files,
      notes: typeof notes === 'string' ? notes : '',
      useIntelligenceContext: useIntelligenceContext === true,
    };

    const submitStart = now();
    const submissionResult = await sendPrompt(submitPayload);
    const submitDuration = elapsedSince(submitStart, now);

    const timings = {
      teamFetchMs: teamContext.durationMs,
      submitMs: submitDuration,
    };

    if (typeof metricsLogger === 'function') {
      safeInvoke(metricsLogger, {
        stage: metricsStage,
        durationMs: submitDuration,
        teamFetchMs: teamContext.durationMs ?? null,
        payloadBytes: estimatePayloadBytes(files),
      });
    }

    return {
      response: submissionResult,
      precisionPrompt,
      context: {
        persona,
        lens: effectiveLens,
        teamId,
        teamName: normalizedTeamName,
        teamInstructions: teamContext.teamInstructions,
        teamType: teamContext.teamType,
      },
      durationMs: submitDuration,
      timings,
      tokens: submissionResult?.tokens || submissionResult?.usage?.total_tokens || null,
    };
  };
}

function callPrecisionBuilder(builder, trimmedMessage, context) {
  if (builder.length >= 2) {
    return builder(trimmedMessage, context);
  }
  return builder({ userInput: trimmedMessage, ...context });
}

function resolveLens(currentLens = 'general', teamType = null, mapper = defaultMapTeamTypeToLens) {
  if (!teamType) {
    return currentLens || 'general';
  }

  if (currentLens && currentLens !== 'general' && currentLens !== 'auto') {
    return currentLens;
  }

  return mapper(teamType) || (typeof teamType === 'string' ? teamType.toLowerCase() : currentLens || 'general');
}

function defaultMapTeamTypeToLens(teamType) {
  if (!teamType) return null;
  return TEAM_TYPE_TO_LENS[teamType] || (typeof teamType === 'string' ? teamType.toLowerCase() : null);
}

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function defaultNow() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function elapsedSince(start, nowFn = defaultNow) {
  const end = nowFn();
  return Number.isFinite(start) ? Math.max(0, end - start) : undefined;
}

function estimatePayloadBytes(files) {
  if (!Array.isArray(files) || !files.length) {
    return 0;
  }
  return files.reduce((total, file) => total + (file?.size || 0), 0);
}

function safeInvoke(fn, payload) {
  try {
    fn(payload);
  } catch (error) {
    // ignore metrics errors to preserve legacy behaviour
  }
}

function defaultCacheKeyResolver(teamId) {
  return teamId || null;
}

if (typeof window !== 'undefined') {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.coach = window.AgileProDomain.coach || {};
  window.AgileProDomain.coach.createSubmitCoachPrompt = createSubmitCoachPrompt;
}
